import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { convertDateField } from '../../services/calculations';
import api from '../../services/api';
import './AddTransaction.css';

function AddTransaction({ settings, onTransactionAdded }) {
  const [opType, setOpType] = useState('ثبت معامله جدید (خرید/فروش)');
  const [transactions, setTransactions] = useState([]);
  const [symbolsList, setSymbolsList] = useState([]);
  
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split('T')[0]);
  const [tradeType, setTradeType] = useState('buy');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [newSymbol, setNewSymbol] = useState('');
  const [marketType, setMarketType] = useState('Bourse');
  const [price, setPrice] = useState(0);
  const [note, setNote] = useState('');
  
  const [openSteps, setOpenSteps] = useState([]);
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [stepQuantities, setStepQuantities] = useState({});
  const [qty, setQty] = useState(1);
  const [stepName, setStepName] = useState('پله 1');
  const [blocked, setBlocked] = useState(0);
  const [contractSize, setContractSize] = useState(1);
  
  const [marginDate, setMarginDate] = useState(new Date().toISOString().split('T')[0]);
  const [marginSymbol, setMarginSymbol] = useState('');
  const [marginAmount, setMarginAmount] = useState(0);
  const [marginStep, setMarginStep] = useState('پله 1');
  const [futureSymbols, setFutureSymbols] = useState([]);
  
  const [bonusDate, setBonusDate] = useState(new Date().toISOString().split('T')[0]);
  const [bonusSymbol, setBonusSymbol] = useState('');
  const [bonusQty, setBonusQty] = useState(1);
  const [bonusStep, setBonusStep] = useState('پله 1');

  // Hami Profit state
  const [hamiDate, setHamiDate] = useState(new Date().toISOString().split('T')[0]);
  const [hamiAmount, setHamiAmount] = useState(0);

  const [backendConnected, setBackendConnected] = useState(true);

  // Order history file upload state
  const [orderFileName, setOrderFileName] = useState('');
  const [parsedOrders, setParsedOrders] = useState([]);
  const [previewOrders, setPreviewOrders] = useState([]);
  const [orderUploading, setOrderUploading] = useState(false);
  const [orderUploadResult, setOrderUploadResult] = useState(null);
  const [orderContractSize, setOrderContractSize] = useState(1000);

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Health check: verify backend connectivity every 30 seconds
  useEffect(() => {
    const checkBackend = async () => {
      try {
        await api.get('/api/settings');
        setBackendConnected(true);
      } catch (error) {
        setBackendConnected(false);
      }
    };
    checkBackend();
    const interval = setInterval(checkBackend, 30000);
    return () => clearInterval(interval);
  }, []);

   // به‌روزرسانی خودکار stepName هنگام تغییر نماد (برای خرید جدید و فروش فزاینده)
  useEffect(() => {
    const currentSymbol = getSymbol();
    // شرط بررسی می‌کند که اگر معامله از نوع خرید یا فروش فزاینده بود، پله جدید محاسبه شود
    if (currentSymbol && (tradeType === 'buy' || tradeType === 'short_sell')) {
      const newStepName = getDefaultStepName(currentSymbol);
      setStepName(newStepName);
    }
  }, [selectedSymbol, newSymbol, transactions, tradeType]);


  useEffect(() => {
    if (tradeType === 'sell' && selectedSymbol && marketType !== 'Future') {
      calculateOpenSteps();
    }
  }, [selectedSymbol, tradeType, marketType, transactions]);

  useEffect(() => {
    const fetchFutureSymbols = async () => {
      try {
        const data = await api.get('/api/transactions');
        const futures = data.filter(t => t.MarketType === 'Future');
        const uniqueSymbols = [...new Set(futures.map(t => t.Symbol))];
        setFutureSymbols(uniqueSymbols);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchFutureSymbols();
  }, [transactions]);

  const fetchTransactions = async () => {
    try {
      const data = await api.get('/api/transactions');
      setTransactions(data);
      const uniqueSymbols = [...new Set(data.map(t => t.Symbol))];
      setSymbolsList(uniqueSymbols);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const normalizePersianText = (text) => {
    if (!text || typeof text !== 'string') return String(text || '');
    const mapping = {
      'ي': 'ی', 'ك': 'ک',
      '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
      '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
    };
    let result = text;
    for (const [k, v] of Object.entries(mapping)) {
      result = result.replace(new RegExp(k, 'g'), v);
    }
    return result.trim();
  };

  // Parse Persian/Arabic numbers to regular numbers
  const parsePersianNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;

    // Convert Persian/Arabic numbers to regular numbers
    const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

    let str = String(value);

    // Replace Persian numbers
    for (let i = 0; i < 10; i++) {
      str = str.replace(new RegExp(persianNumbers[i], 'g'), i.toString());
    }

    // Replace Arabic numbers
    for (let i = 0; i < 10; i++) {
      str = str.replace(new RegExp(arabicNumbers[i], 'g'), i.toString());
    }

    // Remove commas and spaces
    str = str.replace(/[,\s]/g, '');

    const result = parseFloat(str);
    return isNaN(result) ? 0 : result;
  };

  const calculateOpenSteps = async () => {
    try {
      const allTx = await api.get('/api/transactions');
      const symbolTx = allTx.filter(t => t.Symbol === selectedSymbol && t.MarketType !== 'Future');
      
      const stepsMap = {};
      for (const tx of symbolTx) {
        const step = tx.Step;
        if (!stepsMap[step]) {
          stepsMap[step] = { qty: 0, type: '' };
        }
        if (tx.Type === 'buy') {
          stepsMap[step].qty += tx.Quantity;
          stepsMap[step].type = 'buy';
        } else if (tx.Type === 'sell') {
          stepsMap[step].qty -= tx.Quantity;
        } else if (tx.Type === 'bonus') {
          stepsMap[step].qty += tx.Quantity;
        }
      }
      
      const openStepsList = Object.entries(stepsMap)
        .filter(([, data]) => data.qty > 0.001)
        .map(([step, data]) => ({ step: step, remainingQty: data.qty }));
      
      setOpenSteps(openStepsList);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getSymbol = () => {
    return newSymbol || selectedSymbol;
  };

  // تابع محاسبه پله بعدی بر اساس نماد
  const getDefaultStepName = (symbol) => {
    if (!symbol || transactions.length === 0) return 'پله 1';
    
    // فیلتر تراکنش‌های مربوط به این نماد
    const symbolTx = transactions.filter(t => t.Symbol === symbol);
    if (symbolTx.length === 0) return 'پله 1';
    
    // استخراج تمام شماره پله‌ها
    const stepNumbers = [];
    for (const tx of symbolTx) {
      const step = tx.Step;
      if (step) {
        const numbers = step.match(/\d+/g);
        if (numbers && numbers.length > 0) {
          const stepNum = parseInt(numbers[numbers.length - 1]);
          if (!isNaN(stepNum)) {
            stepNumbers.push(stepNum);
          }
        }
      }
    }
    
    if (stepNumbers.length === 0) return 'پله 1';
    
    // پیدا کردن بزرگترین شماره پله
    const maxStep = Math.max(...stepNumbers);
    
    // پله بعدی = بزرگترین شماره + 1
    return `پله ${maxStep + 1}`;
  };

  // شناسایی نگاشت ستون‌های فایل تاریخچه سفارشات
  const detectOrderColumnMapping = (headers) => {
    const mapping = {
      date: -1,
      orderSide: -1,
      symbol: -1,
      totalVolume: -1,
      price: -1
    };

    headers.forEach((header, index) => {
      const h = normalizePersianText(String(header || '')).toLowerCase().trim();

      if (h.includes('تاریخ') || h === 'date') {
        mapping.date = index;
      } else if ((h.includes('سمت') && h.includes('سفارش')) || h.includes('order side') || h.includes('side')) {
        mapping.orderSide = index;
      } else if (h.includes('نماد') || h.includes('symbol')) {
        mapping.symbol = index;
      } else if ((h.includes('حجم') && h.includes('کل')) || h.includes('total volume') || h.includes('volume')) {
        mapping.totalVolume = index;
      } else if (h.includes('قیمت') || h.includes('price')) {
        mapping.price = index;
      }
    });

    return mapping;
  };

  // محاسبه موقعیت باز واقعی یک نماد از تراکنش‌های موجود
  const getOpenPositionQuantity = (symbol) => {
    const symbolTx = transactions.filter(t =>
      t.Symbol === symbol &&
      t.MarketType !== 'Future' &&
      t.Type !== 'margin' &&
      t.Type !== 'hami_profit'
    );

    let netQty = 0;
    for (const tx of symbolTx) {
      if (tx.Type === 'buy' || tx.Type === 'bonus') {
        netQty += parseFloat(tx.Quantity) || 0;
      } else if (tx.Type === 'sell') {
        netQty -= parseFloat(tx.Quantity) || 0;
      }
      // فروش فزاینده موقعیت باز را کاهش نمی‌دهد
    }

    return netQty;
  };

  // تشخیص خودکار نوع بازار از دیتابیس قیمت‌ها
  const detectMarketTypes = async (symbols) => {
    if (symbols.length === 0) return {};

    try {
      const data = await api.get('/api/symbols-market', { symbols: symbols.join(',') });
      return data.marketMap || {};
    } catch (error) {
      console.error('Error detecting market types:', error);
    }

    return {};
  };

  // پردازش فایل تاریخچه سفارشات کارگزاری
  const handleOrderFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setOrderFileName(file.name);
    setParsedOrders([]);
    setPreviewOrders([]);
    setOrderUploadResult(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        if (jsonData.length < 2) {
          alert('فایل خالی است یا داده ندارد');
          return;
        }

        const headers = jsonData[0].map(h => normalizePersianText(String(h || '')));
        const columnMap = detectOrderColumnMapping(headers);

        if (columnMap.date === -1 || columnMap.orderSide === -1 || columnMap.symbol === -1) {
          alert('ستون‌های تاریخ، سمت سفارش و نماد در فایل یافت نشد');
          return;
        }

        const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

        // استخراج ردیف‌های اولیه
        const parsed = dataRows.map((row, index) => {
          const rawDate = String(row[columnMap.date] || '').trim();
          const rawSide = String(row[columnMap.orderSide] || '').trim();
          const rawSymbol = String(row[columnMap.symbol] || '').trim();
          const rawQty = row[columnMap.totalVolume];
          const rawPrice = row[columnMap.price];

          const normalizedSide = normalizePersianText(rawSide);
          const normalizedSymbol = normalizePersianText(rawSymbol);
          const gregorianDate = convertDateField(rawDate);
          const qty = parsePersianNumber(rawQty);
          const price = parsePersianNumber(rawPrice);

          let side = 'buy';
          if (normalizedSide.includes('خرید')) side = 'buy';
          else if (normalizedSide.includes('فروش')) side = 'sell';

          return {
            id: index + 1,
            date: gregorianDate,
            rawDate,
            side,
            rawSide: normalizedSide,
            symbol: normalizedSymbol,
            qty,
            price,
            marketType: 'Bourse',
            type: side,
            note: ''
          };
        });

        // تشخیص نوع بازار برای نمادهای یکتا
        const uniqueSymbols = [...new Set(parsed.map(o => o.symbol).filter(Boolean))];
        const marketMap = await detectMarketTypes(uniqueSymbols);

        // طبقه‌بندی نهایی: نوع بازار، فروش فزاینده، اندازه قرارداد
        const classified = parsed.map(order => {
          let marketType = marketMap[order.symbol] || 'Bourse';

          // Fallback: نمادهای آپشن با پیشوند ض یا ط
          if (marketType === 'Bourse' && (order.symbol.startsWith('ض') || order.symbol.startsWith('ط'))) {
            marketType = 'Option';
          }

          // تشخیص فروش فزاینده: اگر نماد موقعیت باز ندارد
          let type = order.side;
          if (order.side === 'sell') {
            const openQty = getOpenPositionQuantity(order.symbol);
            if (openQty <= 0.001) {
              type = 'short_sell';
            }
          }

          return {
            ...order,
            marketType,
            type
          };
        });

        setParsedOrders(classified);
        setPreviewOrders(classified.slice(0, 100));
      } catch (error) {
        console.error('Error parsing order file:', error);
        alert('خطا در پردازش فایل: ' + error.message);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // ثبت سفارشات استخراج‌شده در بک‌اند
  const handleSubmitOrders = async () => {
    if (parsedOrders.length === 0) {
      alert('هیچ سفارشی برای ثبت وجود ندارد');
      return;
    }

    setOrderUploading(true);
    setOrderUploadResult(null);

    try {
      const rows = parsedOrders.map(order => ({
        Date: order.date,
        Symbol: order.symbol,
        Type: order.type,
        MarketType: order.marketType,
        Quantity: order.marketType === 'Option' ? order.qty * orderContractSize : order.qty,
        Price: order.price,
        BlockedAmount: 0,
        Note: order.note || ''
      }));

      const result = await api.post('/api/transactions/broker-import', rows);

      setOrderUploadResult(result);
      alert(`✅ با موفقیت ${result.insertedCount} سفارش ثبت شد`);
      await fetchTransactions();
      onTransactionAdded();

      setOrderFileName('');
      setParsedOrders([]);
      setPreviewOrders([]);
    } catch (error) {
      console.error('Error submitting orders:', error);
      alert('خطا در ارتباط با سرور');
    }

    setOrderUploading(false);
  };

    const handleSubmitTrade = async () => {
    const symbol = normalizePersianText(getSymbol());
    if (!symbol) {
      alert('لطفاً نماد را وارد کنید');
      return;
    }

        // بررسی ولیدیشن فروش فزاینده: عدم وجود موقعیت خریدِ باز واقعی (فعال) در بازار آپشن برای این نماد
    if (tradeType === 'short_sell') {
      const symbolTransactions = transactions.filter(t => t.Symbol === symbol && t.MarketType === 'Option');
      
      let netOpenQuantity = 0;
      symbolTransactions.forEach(t => {
        if (t.Type === 'buy') {
          netOpenQuantity += parseFloat(t.Quantity) || 0;
        } else if (t.Type === 'sell') {
          netOpenQuantity -= parseFloat(t.Quantity) || 0;
        }
      });

      // اگر پوزیشن خریدِ باز واقعی (بزرگتر از صفر) وجود داشته باشد، خطا صادر می‌شود
      if (netOpenQuantity > 0.001) {
        alert(`شما به دلیل داشتن موقعیت خرید باز فعال به تعداد ${netOpenQuantity} در این نماد مجاز به فروش فزاینده نیستید.`);
        return;
      }
    }

    
    let totalQty = 0;
    const stepsToSubmit = [];
    
    if (tradeType === 'sell' && marketType !== 'Future') {

      if (selectedSteps.length === 0) {
        alert('لطفاً حداقل یک پله را انتخاب کنید');
        return;
      }
      for (const step of selectedSteps) {
        const stepQty = stepQuantities[step] || 0;
        if (stepQty > 0) {
          stepsToSubmit.push({ step: step, qty: stepQty });
          totalQty += stepQty;
        }
      }
    } else {
      // برای خرید، از stepName استفاده کن (که خودکار به‌روز می‌شود)
      const currentStepName = stepName || getDefaultStepName(symbol);
      stepsToSubmit.push({ step: currentStepName, qty: qty });
      totalQty = qty;
    }
    
    if (totalQty <= 0) {
      alert('لطفاً تعداد را مشخص کنید');
      return;
    }
    
    for (const item of stepsToSubmit) {
      let finalQty = item.qty;
      // اعمال اندازه قرارداد برای تمام معاملات آپشن (خرید، فروش و فروش فزاینده)
      if (marketType === 'Option') {
        finalQty = item.qty * contractSize;
      }

      
      const row = {
        Date: tradeDate,
        Symbol: symbol,
        Type: tradeType,
        MarketType: marketType,
        Quantity: finalQty,
        Price: price,
        Step: item.step,
        BlockedAmount: marketType === 'Future' ? blocked : 0,
        Note: note
      };

      
      try {
        await api.post('/api/transactions', row);
      } catch (error) {
        alert('خطا در ثبت تراکنش');
        return;
      }
    }
    
    alert('تراکنش با موفقیت ثبت شد');
    onTransactionAdded();
    await fetchTransactions();
    
    // ریست فرم
    setTradeDate(new Date().toISOString().split('T')[0]);
    setSelectedSymbol('');
    setNewSymbol('');
    setPrice(0);
    setNote('');
    setQty(1);
    setSelectedSteps([]);
    setStepQuantities({});
    
    // stepName بعد از ثبت، برای نماد جدید دوباره محاسبه می‌شود
    // (useEffect خودکار این کار را می‌کند)
  };

  const handleSubmitMargin = async () => {
    if (!marginSymbol) {
      alert('لطفاً نماد را وارد کنید');
      return;
    }
    
    const row = {
      Date: marginDate,
      Symbol: normalizePersianText(marginSymbol),
      Type: 'margin',
      MarketType: 'Future',
      Quantity: 0,
      Price: 0,
      Step: marginStep,
      BlockedAmount: marginAmount,
      Note: 'شارژ مارجین'
    };
    
    try {
      await api.post('/api/transactions', row);
      alert('شارژ با موفقیت ثبت شد');
      onTransactionAdded();
      await fetchTransactions();
      setMarginSymbol('');
      setMarginAmount(0);
    } catch (error) {
      alert('خطا در ثبت شارژ');
    }
  };

  const handleSubmitBonus = async () => {
    if (!bonusSymbol) {
      alert('لطفاً نماد را وارد کنید');
      return;
    }
    
    const row = {
      Date: bonusDate,
      Symbol: normalizePersianText(bonusSymbol),
      Type: 'bonus',
      MarketType: 'Bourse',
      Quantity: bonusQty,
      Price: 0,
      Step: bonusStep,
      BlockedAmount: 0,
      Note: 'جایزه'
    };
    
    try {
      await api.post('/api/transactions', row);
      alert('جایزه با موفقیت ثبت شد');
      onTransactionAdded();
      await fetchTransactions();
      setBonusSymbol('');
      setBonusQty(1);
    } catch (error) {
      alert('خطا در ثبت جایزه');
    }
  };

  // Handler for Hami Profit submission
  const handleSubmitHamiProfit = async () => {
    if (!hamiAmount || hamiAmount <= 0) {
      alert('لطفاً مبلغ سود را وارد کنید');
      return;
    }

    const row = {
      Date: hamiDate,
      Symbol: 'حامی',
      Type: 'hami_profit',
      MarketType: 'Bourse',
      Quantity: 1,
      Price: 0,
      Step: '-',
      BlockedAmount: hamiAmount,
      Note: 'سود حامی'
    };

    try {
      await api.post('/api/transactions', row);
      alert('سود حامی با موفقیت ثبت شد');
      onTransactionAdded();
      await fetchTransactions();
      setHamiAmount(0);
    } catch (error) {
      alert('خطا در ثبت سود حامی');
    }
  };

  const renderTradeForm = () => {
    const symbol = getSymbol();
    // برای نمایش در placeholder
    const suggestedStep = symbol ? getDefaultStepName(symbol) : 'پله 1';
    
    return (
      <div className="trade-form">
        <div className="form-row">
          <div className="form-group">
            <label>تاریخ معامله</label>
            <input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>نوع سفارش</label>
            <select value={tradeType} onChange={(e) => {
              const val = e.target.value;
              setTradeType(val);
              if (val === 'short_sell') {
                setMarketType('Option');
              }
            }}>
              <option value="buy">خرید</option>
              <option value="sell">فروش</option>
              <option value="short_sell">فروش فزاینده</option>
            </select>
          </div>

        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>نام نماد (انتخاب از لیست)</label>
            <select value={selectedSymbol} onChange={(e) => setSelectedSymbol(e.target.value)}>
              <option value="">انتخاب کنید</option>
              {symbolsList.map(sym => <option key={sym} value={sym}>{sym}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>یا وارد کردن نماد جدید</label>
            <input type="text" value={newSymbol} onChange={(e) => setNewSymbol(e.target.value)} placeholder="مثال: وپارس" />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>بازار</label>
            <select value={marketType} onChange={(e) => setMarketType(e.target.value)}>
              <option value="Bourse">بورس</option>
              <option value="FaraBourse">فرابورس</option>
              <option value="LeveragedFund">صندوق اهرمی</option>
              <option value="Gold">طلا</option>
              <option value="Future">آتی</option>
              <option value="Option">اختیار</option>
            </select>
          </div>
          <div className="form-group">
            <label>قیمت واحد (ریال)</label>
            <input type="number" value={price} onChange={(e) => setPrice(parseFloat(e.target.value))} step={100} />
          </div>
        </div>
        
        <div className="form-group">
          <label>یادداشت</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows="2"></textarea>
        </div>
        
        <hr className="divider" />
        
        {tradeType === 'sell' && symbol && marketType !== 'Future' ? (
          <div className="smart-sell">
            <h3>انتخاب پله‌ها برای فروش</h3>
            {openSteps.length === 0 ? (
              <div>
                <div className="warning-box">هیچ پله بازی برای این نماد یافت نشد!</div>
                <div className="form-group">
                  <label>نام پله (دستی)</label>
                  <input type="text" value={stepName} onChange={(e) => setStepName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>تعداد</label>
                  <input type="number" value={qty} onChange={(e) => setQty(parseFloat(e.target.value))} min={1} step={1} />
                </div>
              </div>
            ) : (
              <div>
                <div className="form-group">
                  <label>کدام پله‌ها را می‌فروشید؟</label>
                  <select multiple value={selectedSteps} onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions, option => option.value);
                    setSelectedSteps(values);
                    const newQuantities = {};
                    values.forEach(step => {
                      const stepData = openSteps.find(s => s.step === step);
                      newQuantities[step] = stepData?.remainingQty || 0;
                    });
                    setStepQuantities(newQuantities);
                  }} style={{ height: '120px' }}>
                    {openSteps.map(step => (
                      <option key={step.step} value={step.step}>
                        {step.step} (موجودی: {step.remainingQty})
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedSteps.map(step => {
                  const stepData = openSteps.find(s => s.step === step);
                  return (
                    <div key={step} className="form-group">
                      <label>تعداد فروش از {step} (موجودی: {stepData?.remainingQty})</label>
                      <input 
                        type="number" 
                        value={stepQuantities[step] || 0}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          const maxVal = stepData?.remainingQty || 0;
                          setStepQuantities(prev => ({ ...prev, [step]: Math.min(val, maxVal) }));
                        }}
                        min={0}
                        max={stepData?.remainingQty}
                        step={1}
                      />
                    </div>
                  );
                })}
                
                {selectedSteps.length > 0 && (
                  <div className="info-box">
                    جمع کل تعداد برای ثبت: {Object.values(stepQuantities).reduce((a, b) => a + b, 0)}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="normal-form">
            <div className="form-row">
              <div className="form-group">
                <label>نام پله</label>
                <input 
                  type="text" 
                  value={stepName} 
                  onChange={(e) => setStepName(e.target.value)} 
                  placeholder={suggestedStep}
                />
                {symbol && (
                  <small className="step-hint">پیشنهاد: {suggestedStep}</small>
                )}
              </div>
              <div className="form-group">
                <label>تعداد/حجم (تعداد قرارداد)</label>
                <input type="number" value={qty} onChange={(e) => setQty(parseFloat(e.target.value))} min={1} step={1} />
              </div>
            </div>
            
                        {(marketType === 'Future' || marketType === 'Option') && (
              <div className="form-row">
                {marketType === 'Future' && (
                  <div className="form-group">
                    <label>وجه تضمین (فقط آتی)</label>
                    <input type="number" value={blocked} onChange={(e) => setBlocked(parseFloat(e.target.value))} step={100000} />
                  </div>
                )}
                {marketType === 'Option' && (
                  <div className="form-group">
                    <label>اندازه قرارداد (آپشن)</label>
                    <input type="number" value={contractSize} onChange={(e) => setContractSize(parseFloat(e.target.value))} min={1} step={1} />
                  </div>
                )}
              </div>
            )}

          </div>
        )}
        
        <button className="submit-btn" onClick={handleSubmitTrade}>
          ثبت تراکنش
        </button>
      </div>
    );
  };

  const renderMarginForm = () => {
    return (
      <div className="margin-form">
        <div className="info-box">اضافه کردن وجه تضمین به پوزیشن‌های باز (مخصوص آتی)</div>
        
        <div className="form-row">
          <div className="form-group">
            <label>تاریخ شارژ</label>
            <input type="date" value={marginDate} onChange={(e) => setMarginDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>نماد</label>
            {futureSymbols.length > 0 ? (
              <select value={marginSymbol} onChange={(e) => setMarginSymbol(e.target.value)}>
                <option value="">انتخاب کنید</option>
                {futureSymbols.map(sym => <option key={sym} value={sym}>{sym}</option>)}
              </select>
            ) : (
              <input type="text" value={marginSymbol} onChange={(e) => setMarginSymbol(e.target.value)} placeholder="نماد" />
            )}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>مبلغ شارژ</label>
            <input type="number" value={marginAmount} onChange={(e) => setMarginAmount(parseFloat(e.target.value))} step={100000} />
          </div>
          <div className="form-group">
            <label>مرتبط با پله؟</label>
            <input type="text" value={marginStep} onChange={(e) => setMarginStep(e.target.value)} />
          </div>
        </div>
        
        <button className="submit-btn" onClick={handleSubmitMargin}>
          ثبت شارژ
        </button>
      </div>
    );
  };

  const renderBonusForm = () => {
    return (
      <div className="bonus-form">
        <div className="info-box">ثبت سهام جایزه (قیمت خرید 0)</div>
        
        <div className="form-row">
          <div className="form-group">
            <label>تاریخ</label>
            <input type="date" value={bonusDate} onChange={(e) => setBonusDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>نماد</label>
            {symbolsList.length > 0 ? (
              <select value={bonusSymbol} onChange={(e) => setBonusSymbol(e.target.value)}>
                <option value="">انتخاب کنید</option>
                {symbolsList.map(sym => <option key={sym} value={sym}>{sym}</option>)}
              </select>
            ) : (
              <input type="text" value={bonusSymbol} onChange={(e) => setBonusSymbol(e.target.value)} placeholder="نماد" />
            )}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>تعداد جایزه</label>
            <input type="number" value={bonusQty} onChange={(e) => setBonusQty(parseFloat(e.target.value))} min={1} step={1} />
          </div>
          <div className="form-group">
            <label>روی کدام پله؟</label>
            <input type="text" value={bonusStep} onChange={(e) => setBonusStep(e.target.value)} />
          </div>
        </div>
        
        <button className="submit-btn" onClick={handleSubmitBonus}>
          ثبت جایزه
        </button>
      </div>
    );
  };

  // Hami Profit Form
  const renderHamiProfitForm = () => {
    return (
      <div className="hami-form">
        <div className="info-box">ثبت سود حامی (سود نقدی از کارگزاری)</div>

        <div className="form-row">
          <div className="form-group">
            <label>تاریخ دریافت سود</label>
            <input type="date" value={hamiDate} onChange={(e) => setHamiDate(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>مبلغ سود (ریال)</label>
            <input
              type="number"
              value={hamiAmount || ''}
              onChange={(e) => setHamiAmount(parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
              placeholder="مبلغ به ریال"
            />
          </div>
        </div>

        {hamiAmount > 0 && (
          <div className="preview-box">
            <span>مبلغ قابل نمایش: </span>
            <strong>{hamiAmount.toLocaleString()} ریال</strong>
          </div>
        )}

        <button className="submit-btn" onClick={handleSubmitHamiProfit}>
          ثبت سود حامی
        </button>
      </div>
    );
  };

  return (
    <div className="add-transaction">
      <h1>ثبت عملیات جدید</h1>

      {!backendConnected && (
        <div className="warning-box" style={{ marginBottom: '20px' }}>
          ⚠️ سرور بک‌اند در پورت 5000 در دسترس نیست. لطفاً آن را راه‌اندازی کنید:
          <br />
          <code>npm run server</code> یا <code>npm start</code>
        </div>
      )}

      <div className="op-type-selector">
        <label>نوع عملیات</label>
        <select value={opType} onChange={(e) => setOpType(e.target.value)}>
          <option value="ثبت معامله جدید (خرید/فروش)">ثبت معامله جدید (خرید/فروش)</option>
          <option value="ثبت سود حامی">ثبت سود حامی</option>
          <option value="شارژ حساب (مارجین)">شارژ حساب (مارجین)</option>
          <option value="ثبت سهام جایزه">ثبت سهام جایزه</option>
        </select>
      </div>
      
      <div className="form-container">
        {opType === 'ثبت معامله جدید (خرید/فروش)' && (
          <>
            <div className="excel-import-form">
              <h3>📤 آپلود تاریخچه سفارشات کارگزاری</h3>
              <div className="info-box">
                فایل اکسل تاریخچه سفارشات را آپلود کنید. ستون‌های تاریخ، سمت سفارش، نماد، حجم کل و قیمت به صورت خودکار شناسایی می‌شوند. فروش فزاینده نیز بر اساس موقعیت باز موجود در دیتابیس تشخیص داده می‌شود.
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>انتخاب فایل اکسل</label>
                  <input type="file" accept=".xlsx,.xls" onChange={handleOrderFileUpload} disabled={orderUploading} />
                </div>
                <div className="form-group">
                  <label>اندازه قرارداد آپشن (پیش‌فرض)</label>
                  <input type="number" value={orderContractSize} onChange={(e) => setOrderContractSize(parseFloat(e.target.value) || 1000)} min={1} step={100} />
                  <small className="step-hint">برای سایر بازارها تأثیری ندارد</small>
                </div>
              </div>
              {orderFileName && (
                <div className="info-box">
                  📄 فایل انتخاب شده: <strong>{orderFileName}</strong>
                  <br />
                  <small>تعداد کل سفارشات: {parsedOrders.length}</small>
                </div>
              )}
            </div>

            {previewOrders.length > 0 && (
              <div className="excel-preview">
                <h3>🔍 پیش‌نمایش سفارشات ({previewOrders.length} از {parsedOrders.length})</h3>
                <div className="detected-summary">
                  <span className="badge badge-buy">خرید: {parsedOrders.filter(o => o.type === 'buy').length}</span>
                  <span className="badge badge-sell">فروش: {parsedOrders.filter(o => o.type === 'sell').length}</span>
                  <span className="badge badge-short-sell">فروش فزاینده: {parsedOrders.filter(o => o.type === 'short_sell').length}</span>
                </div>
                <div className="table-scroll">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>تاریخ</th>
                        <th>نماد</th>
                        <th>نوع</th>
                        <th>بازار</th>
                        <th>تعداد فایل</th>
                        <th>تعداد نهایی</th>
                        <th>قیمت</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewOrders.map((order) => (
                        <tr key={order.id} className={`row-${order.type}`}>
                          <td>{order.id}</td>
                          <td>{order.date}</td>
                          <td>{order.symbol}</td>
                          <td>
                            {order.type === 'buy' && <span className="badge badge-buy">خرید</span>}
                            {order.type === 'sell' && <span className="badge badge-sell">فروش</span>}
                            {order.type === 'short_sell' && <span className="badge badge-short-sell">فروش فزاینده</span>}
                          </td>
                          <td>{order.marketType}</td>
                          <td>{order.qty.toLocaleString()}</td>
                          <td>{(order.marketType === 'Option' ? order.qty * orderContractSize : order.qty).toLocaleString()}</td>
                          <td>{order.price.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedOrders.length > 100 && (
                  <div className="info-box">⚠️ فقط 100 رکورد اول نمایش داده شده است.</div>
                )}
                <div className="button-group">
                  <button className="submit-btn" onClick={handleSubmitOrders} disabled={orderUploading || parsedOrders.length === 0}>
                    {orderUploading ? 'در حال ثبت...' : `ثبت ${parsedOrders.length} سفارش`}
                  </button>
                  <button className="btn-rollback" onClick={() => { setOrderFileName(''); setParsedOrders([]); setPreviewOrders([]); setOrderUploadResult(null); }}>
                    پاک کردن
                  </button>
                </div>
              </div>
            )}

            {orderUploadResult && (
              <div className="info-box success">
                ✅ با موفقیت {orderUploadResult.insertedCount} سفارش ثبت شد.
                {orderUploadResult.shortSells > 0 && <div>فروش فزاینده: {orderUploadResult.shortSells} مورد</div>}
              </div>
            )}

            {renderTradeForm()}
          </>
        )}
        {opType === 'شارژ حساب (مارجین)' && renderMarginForm()}
        {opType === 'ثبت سود حامی' && renderHamiProfitForm()}
        {opType === 'ثبت سهام جایزه' && renderBonusForm()}
      </div>
    </div>
  );
}

export default AddTransaction;